<template>
  <v-container>
    <v-card flat color="transparent">
      <v-card-title v-once v-text="$dictionary.get('checkout-title')" />
      <v-card-subtitle v-if="settings.delivery_notes">
        {{ settings.delivery_notes }}
      </v-card-subtitle>
      <v-card-subtitle>
        <CheckoutTable :delivery="deliveryPrice" />
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="sum > 0 && deliveryPrice > 0" type="info">
          <div>
            {{ $dictionary.get('delivery-price-is') }}
            <b>{{ $tools.money(deliveryPrice) }}</b>
          </div>
          <div v-if="deliveryPrice2Free">
            {{ $dictionary.get('delivery-free-to-go-1') }}
            <b>{{ $tools.money(deliveryPrice2Free) }}</b>
            {{ $dictionary.get('delivery-free-to-go-2') }}
          </div>
        </v-alert>
      </v-card-text>
      <v-card-text>
        <CheckoutForm :fields="fields"
                      :values="order"
                      :errors="errors"
                      :point.sync="point"
                      :address="address.text"
                      @input="setValue"
        />
        <AlertTimeRange :point="point" />
        <Button v-if="canOrder"
                :loading="loading"
                title="send-order"
                @click="send"
                @click.native="$ym.do('checkout_send')"
        />
        <Button :loading="loading"
                subtitle="go-to-showcase"
                sub-button
                to="showcase"
                @click.native="$ym.do('checkout_go_back')"
        />
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import CheckoutTable from '@/components/CheckoutTable';
import CheckoutForm from '@/components/CheckoutForm';
import AlertTimeRange from '@/components/AlertTimeRange';
import Button from '@/components/Button';

export default {
  name: 'Checkout',
  components: { Button, AlertTimeRange, CheckoutTable, CheckoutForm },
  data: () => ({
    data: {},
    errors: {},
    deliveryPrice: 0,
    deliveryPrice2Free: 0,
    orderPrice: 0,
    loading: false
  }),
  computed: {
    point() {
      return this.$store.state.data.point;
    },
    pointData() {
      return this.$store.state.data.points[this.point];
    },
    pickup() {
      return this.$store.state.data.mode === 'pickup';
    },
    order() {
      return this.$store.state.data.order;
    },
    positions() {
      return this.$store.state.basket.items.length;
    },
    fields() {
      const all = [];
      if (!this.pickup) {
        all.push({ key: 'address_entrance', obligatory: false, cols: 4 });
        all.push({ key: 'address_floor', obligatory: false, cols: 4 });
        all.push({ key: 'address_number', obligatory: false, cols: 4 });
      }
      all.push({ key: 'name', obligatory: true, cols: 12 });
      all.push({ key: 'phone', obligatory: true, cols: 12 });
      all.push({ key: 'email', obligatory: null, cols: 12 });
      all.push({ key: 'notes', obligatory: null, cols: 12 });
      all.push({ key: 'deliverToHall', obligatory: false, cols: 12, type: 'checkbox' });

      if (this.settings.promo * 1 > 0) {
        all.push({ key: 'promo', obligatory: false });
      }
      const filtered = all.filter((field) => this.settings[`form_${field.key}`] !== 'no');
      filtered.forEach((field, i) => {
        if (field.obligatory === null) {
          filtered[i].obligatory = this.settings[`form_${field.key}`] === 'obligatory';
        }
      });
      return filtered;
    },
    settings() {
      return this.$store.state.data.settings;
    },
    canOrder() {
      return this.positions > 0 && this.sum >= this.orderPrice;
    },
    sum() {
      return this.$store.getters['basket/totals'].price;
    },
    address() {
      if (this.pickup) {
        return {};
      }
      return this.$store.state.data.address;
    }
  },
  watch: {
    sum() {
      this.update();
    }
  },
  mounted() {
    this.update();
  },
  methods: {
    update() {
      if (!this.pickup) {
        const { area } = this.$store.state.data;
        const areaData = this.pointData.areas.find((item) => item._id === area);
        const { order_sum_threshold, delivery_free_threshold } = this.pointData;
        this.orderPrice = order_sum_threshold;
        this.deliveryPrice = this.sum < delivery_free_threshold ? areaData.price : 0;
        this.deliveryPrice2Free = delivery_free_threshold - this.sum;
      } else {
        this.orderPrice = 0;
        this.deliveryPrice = 0;
        this.deliveryPrice2Free = 0;
      }
    },
    setValue({ field, value }) {
      this.$set(this.errors, field, null);
      return this.$store.commit('data/order', { field, value });
    },
    checkObligatory() {
      let ok = true;
      const { order } = this.$store.state.data || {};
      this.fields.forEach(({ key, obligatory }) => {
        if (obligatory && !order[key]) {
          ok = false;
          this.$set(this.errors, key, this.$dictionary.get('order-obligatory'));
        } else {
          this.$set(this.errors, key, null);
        }
      });
      return ok;
    },
    async send(e) {
      if (!this.checkObligatory()) return;
      this.loading = true;
      const { order } = this.$store.state.data;
      const items = this.$store.state.basket.items.map((item) => ({
        item: item.commodity._id,
        quantity: item.quantity,
        price: item.commodity.priceFinal,
        name: item.commodity.name
      }));
      const values = {
        ...order,
        address: {
          ...this.address,
          entrance: order.address_entrance,
          floor: order.address_floor,
          number: order.address_number,
        },
        price_full: this.sum + this.deliveryPrice,
        price_delivery: this.deliveryPrice,
        point: this.pointData._id,
        items,
        mode: this.pickup ? 'pickup' : 'delivery',
        payment_method: order.payment_method,
      };
      const hash = await this.$essentia.fetch('shop/add-order', values);
      if (!e.shiftKey) {
        this.$store.commit('basket/clear');
        this.$router.push({ name: 'order', params: { hash } });
      }
      this.loading = false;
    }
  }
};
</script>
